import type { RadarFrame } from '@repo/shared';
import type { RadarAnimation } from '../features/radar/useRadarAnimation';
import { formatFrameDateTime, formatFrameOffset, formatFrameTime } from '../lib/format';
import styles from './RadarTimeline.module.css';

export interface RadarTimelineProps {
  frames: readonly RadarFrame[];
  animation: RadarAnimation;
  /** Feed generation time (Unix seconds) — the "now" that offsets are relative to. */
  referenceTime: number;
}

/**
 * Transport controls for the radar loop: play/pause, a scrubber, and the valid
 * time of the frame on screen.
 *
 * Forecast frames are marked three times over — a hatched section of track, a
 * different colour, and a badge on the readout — and that repetition is
 * deliberate. An extrapolated frame is pixels that look exactly like measured
 * radar, and someone glancing at a map to decide whether to leave the house
 * should not have to notice a small word to know which one they're looking at.
 */
export function RadarTimeline({ frames, animation, referenceTime }: RadarTimelineProps) {
  const { index, isPlaying, toggle, seek, step } = animation;
  const active = frames[index];

  if (!active) return null;

  const isForecast = active.kind === 'nowcast';
  const lastObservedIndex = frames.reduce(
    (last, frame, i) => (frame.kind === 'past' ? i : last),
    -1,
  );
  // Where the observed/forecast boundary sits along the track, as a percentage.
  const forecastStart =
    lastObservedIndex >= 0 && lastObservedIndex < frames.length - 1
      ? ((lastObservedIndex + 0.5) / (frames.length - 1)) * 100
      : 100;

  return (
    <div className={styles.timeline} data-forecast={isForecast}>
      <div className={styles.readout}>
        <span className={styles.time} title={formatFrameDateTime(active.time)}>
          {formatFrameTime(active.time)}
        </span>
        <span className={styles.offset}>{formatFrameOffset(active.time, referenceTime)}</span>
        {isForecast && <span className={styles.forecast}>forecast</span>}
      </div>

      <div className={styles.transport}>
        <button
          type="button"
          className={styles.play}
          onClick={toggle}
          aria-label={isPlaying ? 'Pause radar loop' : 'Play radar loop'}
          aria-pressed={isPlaying}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>

        <div className={styles.track}>
          <div className={styles.sliderWrap}>
            {/* Hatches the forecast portion of the track behind the input. */}
            <div
              className={styles.forecastZone}
              style={{ left: `${forecastStart}%` }}
              aria-hidden="true"
            />

            {/* One tick per frame: the loop's length, made countable. */}
            <div className={styles.ticks} aria-hidden="true">
              {frames.map((frame, i) => (
                <span
                  key={frame.time}
                  className={styles.tick}
                  data-kind={frame.kind}
                  data-active={i === index}
                />
              ))}
            </div>

            <input
              type="range"
              className={styles.slider}
              min={0}
              max={Math.max(frames.length - 1, 0)}
              step={1}
              value={index}
              onChange={(event) => seek(Number(event.target.value))}
              onKeyDown={(event) => {
                // Let the arrow keys step frames rather than scrub by input step,
                // so wrapping at the ends behaves the same as the step buttons.
                if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                  event.preventDefault();
                  step(event.key === 'ArrowLeft' ? -1 : 1);
                }
              }}
              aria-label="Radar frame"
              aria-valuetext={`${formatFrameTime(active.time)}, ${formatFrameOffset(active.time, referenceTime)}${isForecast ? ', forecast' : ''}`}
            />
          </div>

          <div className={styles.bounds} aria-hidden="true">
            <span>{formatFrameTime(frames[0]!.time)}</span>
            <span>{formatFrameTime(frames[frames.length - 1]!.time)}</span>
          </div>
        </div>

        <div className={styles.steppers}>
          <button
            type="button"
            className={styles.step}
            onClick={() => step(-1)}
            aria-label="Previous frame"
          >
            <ChevronIcon direction="left" />
          </button>
          <button
            type="button"
            className={styles.step}
            onClick={() => step(1)}
            aria-label="Next frame"
          >
            <ChevronIcon direction="right" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
      <path d="M4.5 2.6v10.8L13.5 8z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
      <rect x="4" y="2.5" width="2.8" height="11" rx="0.8" />
      <rect x="9.2" y="2.5" width="2.8" height="11" rx="0.8" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={direction === 'left' ? 'M10 3.5 5.5 8l4.5 4.5' : 'M6 3.5 10.5 8 6 12.5'} />
    </svg>
  );
}
